import { BadgeVariant } from "shared/components";

export enum FraudRiskLevel {
  Low = "LOW",
  Medium = "MEDIUM",
  High = "HIGH"
}

type FraudRiskBadge = {
  variant: BadgeVariant;
  title: string;
};

export const FRAUD_RISK_BADGE_MAP: Record<FraudRiskLevel, FraudRiskBadge> = {
  [FraudRiskLevel.Low]: {
    variant: BadgeVariant.SUCCESS,
    title: "Low"
  },
  [FraudRiskLevel.Medium]: {
    variant: BadgeVariant.WARNING,
    title: "Medium"
  },
  [FraudRiskLevel.High]: {
    variant: BadgeVariant.ERROR,
    title: "High"
  }
};

type RiskAssessment = {
  risk_level?: string | null;
  summary?: string | null;
};

type InscribePayload = {
  risk_assessment?: RiskAssessment | null;
  risk_assessment_summary?: string | null;
  risk_level?: string | null;
  trust_score?: number | null;
  is_fraudulent?: boolean | null;
  is_amount_verified?: boolean | null;
};

type FraudRiskSource = {
  raw_data?: InscribePayload | null;
  additional_data?: InscribePayload | null;
  sources?: ({ trust_score?: number | null } | null)[] | null;
  trust_score?: number | null;
  rejection_reason?: string[] | string | null;
};

const toRiskLevel = (value?: string | null): FraudRiskLevel | null => {
  if (!value) return null;

  const normalized = value.toUpperCase();

  if (normalized === FraudRiskLevel.Low) return FraudRiskLevel.Low;
  if (normalized === FraudRiskLevel.Medium) return FraudRiskLevel.Medium;
  if (normalized === FraudRiskLevel.High) return FraudRiskLevel.High;

  return null;
};

const toBooleanRiskLevel = (value?: boolean | null): FraudRiskLevel | null => {
  if (typeof value !== "boolean") return null;

  return value ? FraudRiskLevel.High : FraudRiskLevel.Low;
};

const firstOf = <T>(
  sources: (InscribePayload | null | undefined)[],
  pick: (source: InscribePayload) => T | null
): T | null => {
  for (const source of sources) {
    if (!source) continue;

    const value = pick(source);
    if (value !== null) return value;
  }

  return null;
};

export const resolveFraudRisk = (row: unknown): FraudRiskLevel | null => {
  const { raw_data, additional_data } = (row ?? {}) as FraudRiskSource;
  const sources = [raw_data, additional_data];

  return (
    firstOf(
      sources,
      (source) =>
        toRiskLevel(source.risk_assessment?.risk_level) ??
        toRiskLevel(source.risk_level)
    ) ?? firstOf(sources, (source) => toBooleanRiskLevel(source.is_fraudulent))
  );
};

export const resolveAuthenticityScore = (row: unknown): number | null => {
  const { raw_data, additional_data, sources, trust_score } = (row ??
    {}) as FraudRiskSource;

  for (const source of [raw_data, additional_data])
    if (typeof source?.trust_score === "number") return source.trust_score;

  if (typeof trust_score === "number") return trust_score;

  for (const source of sources ?? [])
    if (typeof source?.trust_score === "number") return source.trust_score;

  return null;
};

type AssetSource = {
  asset?: { asset?: { url?: string | null } | null } | null;
};

export const resolveDocumentUrl = (row: unknown): string | undefined =>
  (row as AssetSource)?.asset?.asset?.url ?? undefined;

export const resolveRiskSummary = (row: unknown): string | null => {
  const { raw_data, additional_data } = (row ?? {}) as FraudRiskSource;

  for (const source of [additional_data, raw_data]) {
    const summary =
      source?.risk_assessment_summary ?? source?.risk_assessment?.summary;
    if (summary) return summary;
  }

  return null;
};

const AMOUNT_NOT_VERIFIED = "amount_not_verified";

export const isAmountNotVerified = (row: unknown): boolean => {
  const { additional_data, raw_data, rejection_reason } = (row ??
    {}) as FraudRiskSource;

  const reasons =
    typeof rejection_reason === "string"
      ? [rejection_reason]
      : (rejection_reason ?? []);

  if (reasons.includes(AMOUNT_NOT_VERIFIED)) return true;

  for (const source of [additional_data, raw_data])
    if (source?.is_amount_verified === false) return true;

  return false;
};
